/**
 * Adaugarea unei plati.
 *
 * Ecranul zilei putea pana acum sa citeasca si sa modifice randuri, dar nu sa creeze niciunul:
 * singurul buton `Adauga o plata` traia in starea goala si chema `deschideDrawer(null)`, care
 * STERGE parametrul `drawer` din URL. Nu deschidea nimic. Iar pe server nu exista nicio ruta de
 * creare, deci nici nu ar fi avut ce sa cheme.
 *
 * Trei decizii, toate din felul in care greseste cineva care introduce plati toata ziua:
 *
 *   1. **Furnizorul se cauta, nu se alege dintr-un `<select>` cu 691 de optiuni.** Cautarea se face
 *      pe server (`GET /furnizori?q=`), cu debounce, si arata CUI-ul langa nume — doua firme din
 *      grup au nume aproape identice, iar CUI-ul e singurul lucru care le desparte sigur.
 *   2. **Cursul apare doar la valuta, si numai cand chiar e nevoie.** Pentru RON campul nici nu
 *      exista. Serverul refuza o plata in valuta fara curs si fara BNR — mesajul lui ajunge intact
 *      in formular, nu inlocuit cu un „a esuat" generic.
 *   3. **Nimic nu se scrie optimist.** Randul nu are inca id si nici suma in lei calculata cu cursul
 *      serverului, iar cererea poate fi refuzata din patru motive reale. Un rand care apare si
 *      dispare dupa 300 ms sperie mai tare decat o asteptare de 300 ms.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { formatDayLabel } from '@evomag/core/format';

import { AmountInput } from '../../components/AmountInput.tsx';
import { useFurnizori, type FurnizorLista } from '../../hooks/useFurnizori.ts';
import { useAdaugaPlata } from '../../hooks/useZi.ts';
import { citesteCurs, textDinMinor } from './plata-noua.ts';
import type { Valuta } from '../../hooks/useReferinta.ts';

export interface PropsDialogPlataNoua {
  data: string;
  /** Runda in care aterizeaza plata. `null` = ziua nu are inca runde; serverul creeaza una. */
  runId: number | null;
  /** Eticheta rundei, ca omul sa vada UNDE adauga cand ziua are doua runde. */
  etichetaRunda: string | null;
  valute: readonly Valuta[];
  onClose: () => void;
  /** Se cheama dupa creare, cu id-ul randului nou, ca apelantul sa-l poata evidentia. */
  onCreat: (entryId: number) => void;
}

const DEBOUNCE_MS = 250;

export function DialogPlataNoua({
  data,
  runId,
  etichetaRunda,
  valute,
  onClose,
  onCreat,
}: PropsDialogPlataNoua) {
  const refPanou = useRef<HTMLDivElement>(null);
  const refCautare = useRef<HTMLInputElement>(null);
  const idLista = useId();

  const [textCautare, setTextCautare] = useState('');
  const [interogare, setInterogare] = useState('');
  const [furnizor, setFurnizor] = useState<FurnizorLista | null>(null);
  const [sumaMinor, setSumaMinor] = useState<number | null>(null);
  const [sumaText, setSumaText] = useState<string | null>(null);
  const [moneda, setMoneda] = useState('RON');
  const [cursText, setCursText] = useState('');
  const [scadenta, setScadenta] = useState('');
  const [comentariu, setComentariu] = useState('');

  const adauga = useAdaugaPlata();

  // Focus pe cautare la deschidere: primul lucru de facut e mereu „care furnizor".
  useEffect(() => {
    refCautare.current?.focus();
  }, []);

  // Debounce, ca sa nu plece o cerere per tasta catre o ruta care agrega 691 de furnizori.
  useEffect(() => {
    const t = setTimeout(() => setInterogare(textCautare.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [textCautare]);

  const cautare = useFurnizori(
    { q: interogare, limit: 8 },
    { activ: interogare.length >= 2 && furnizor === null },
  );
  const rezultate = cautare.data?.suppliers ?? [];

  const scalaMinora = valute.find((v) => v.code === moneda)?.minorScale ?? 100;
  const eValuta = moneda !== 'RON';
  const cursE6 = citesteCurs(cursText);
  const cursInvalid = eValuta && cursText.trim() !== '' && cursE6 === null;

  const poateTrimite =
    furnizor !== null && sumaMinor !== null && sumaMinor !== 0 && !cursInvalid && !adauga.isPending;

  function trimite(): void {
    if (!poateTrimite || furnizor === null) return;
    adauga.mutate(
      {
        data,
        plata: {
          ...(runId === null ? {} : { runId }),
          supplierId: furnizor.id,
          // Se trimite TEXTUL, nu cifra deja rotunjita: `parseAmountInput` de pe server e singurul
          // loc unde o suma devine unitati minore, iar expresia `=1200+340,50` trebuie sa ajunga
          // intreaga in `amount_expression`, altfel exportul nu mai poate regenera formula.
          amount: sumaText ?? String(sumaMinor),
          currency: moneda,
          ...(eValuta && cursE6 !== null ? { rateE6: cursE6 } : {}),
          ...(scadenta === '' ? {} : { dueDate: scadenta }),
          ...(comentariu.trim() === '' ? {} : { comment: comentariu.trim() }),
        },
      },
      {
        onSuccess: (raspuns) => {
          onCreat(raspuns.entry.id);
          onClose();
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={refPanou}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`Adaugă o plată pentru ${formatDayLabel(data)}`}
        className="dialog flex w-full max-w-xl flex-col gap-3 rounded-[var(--radius-lg)] p-4"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
      >
        <header className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <h2 className="text-16">Adaugă o plată · {formatDayLabel(data)}</h2>
            <p className="text-11 text-ink-2">
              {etichetaRunda === null
                ? 'Ziua nu are încă nicio rundă — se creează una principală.'
                : `Intră în runda „${etichetaRunda}”.`}
            </p>
          </div>
          <button type="button" className="hit-24 chip" onClick={onClose} aria-label="Închide">
            <X size={12} aria-hidden="true" />
          </button>
        </header>

        {/* --- furnizorul ------------------------------------------------------------------- */}
        <div className="flex flex-col gap-1">
          <label className="text-11 text-ink-2" htmlFor={`${idLista}-q`}>
            Furnizor
          </label>
          {furnizor === null ? (
            <>
              <input
                ref={refCautare}
                id={`${idLista}-q`}
                type="search"
                className="input"
                placeholder="Nume sau CUI…"
                autoComplete="off"
                role="combobox"
                aria-expanded={rezultate.length > 0}
                aria-controls={idLista}
                value={textCautare}
                onChange={(e) => setTextCautare(e.target.value)}
              />
              <ul id={idLista} role="listbox" aria-label="Furnizori găsiți" className="flex flex-col">
                {interogare.length >= 2 && cautare.isPending ? (
                  <li className="text-11 text-ink-3 p-1">Caut…</li>
                ) : null}
                {interogare.length >= 2 && !cautare.isPending && rezultate.length === 0 ? (
                  <li className="text-11 text-ink-3 p-1">
                    Niciun furnizor pentru „{interogare}”. Furnizorii se adaugă prin import, nu de
                    aici.
                  </li>
                ) : null}
                {rezultate.map((f) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected="false"
                      className="hit-24 flex w-full items-baseline justify-between gap-2 rounded-[var(--radius-sm)] px-1 text-left hover:bg-hovered"
                      onClick={() => {
                        setFurnizor(f);
                        if (f.currencyDefault) setMoneda(f.currencyDefault);
                      }}
                    >
                      <span className="text-cell truncate">{f.name}</span>
                      <span className="num text-11 text-ink-3 shrink-0">{f.cuiNorm ?? '—'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] border border-hairline p-1">
              <span className="flex flex-col">
                <span className="text-cell">{furnizor.name}</span>
                <span className="num text-11 text-ink-3">{furnizor.cuiNorm ?? 'fără CUI'}</span>
              </span>
              <button
                type="button"
                className="hit-24 chip"
                onClick={() => {
                  setFurnizor(null);
                  setTextCautare('');
                }}
              >
                Schimbă
              </button>
            </div>
          )}
        </div>

        {/* --- suma, moneda, cursul --------------------------------------------------------- */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex min-w-[12rem] grow flex-col gap-1">
            <span className="text-11 text-ink-2">Sumă</span>
            <AmountInput
              value={sumaMinor}
              scalaMinora={scalaMinora}
              moneda={moneda === 'RON' ? 'lei' : moneda.toLowerCase()}
              eticheta="Suma plății"
              onCommit={(minor, expresie) => {
                setSumaMinor(minor);
                setSumaText(expresie ?? (minor === null ? null : textDinMinor(minor, scalaMinora)));
              }}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-11 text-ink-2" htmlFor={`${idLista}-moneda`}>
              Monedă
            </label>
            <select
              id={`${idLista}-moneda`}
              className="input"
              value={moneda}
              onChange={(e) => setMoneda(e.target.value)}
            >
              {valute
                .filter((v) => v.isActive)
                .map((v) => (
                  <option key={v.code} value={v.code}>
                    {v.code}
                  </option>
                ))}
            </select>
          </div>

          {eValuta ? (
            <div className="flex flex-col gap-1">
              <label className="text-11 text-ink-2" htmlFor={`${idLista}-curs`}>
                Curs
              </label>
              <input
                id={`${idLista}-curs`}
                className="input num w-28"
                inputMode="decimal"
                placeholder="5,2600"
                aria-invalid={cursInvalid}
                aria-describedby={`${idLista}-curs-ajutor`}
                value={cursText}
                onChange={(e) => setCursText(e.target.value)}
              />
              <span id={`${idLista}-curs-ajutor`} className="text-11 text-ink-3">
                {cursInvalid ? 'Curs invalid.' : 'Gol = cursul BNR al zilei.'}
              </span>
            </div>
          ) : null}
        </div>

        {/* --- scadenta --------------------------------------------------------------------- */}
        <div className="flex flex-col gap-1">
          <label className="text-11 text-ink-2" htmlFor={`${idLista}-scadenta`}>
            Scadență <span className="text-ink-3">(opțional)</span>
          </label>
          <input
            id={`${idLista}-scadenta`}
            type="date"
            className="input w-44"
            value={scadenta}
            onChange={(e) => setScadenta(e.target.value)}
          />
          <span className="text-11 text-ink-3">
            Data la care factura e scadentă — nu se schimbă niciodată la amânare.
          </span>
        </div>

        {/* --- comentariu ------------------------------------------------------------------
            Scrie in ACELASI fir pe care il arata drawerul, nu intr-o a doua casuta de note. Doua
            campuri de comentarii pentru aceeasi plata ar diverge de la prima zi, iar cel pe care
            nu-l vezi ar fi mereu cel cu informatia care lipseste. */}
        <div className="flex flex-col gap-1">
          <label className="text-11 text-ink-2" htmlFor={`${idLista}-comentariu`}>
            Comentariu <span className="text-ink-3">(opțional)</span>
          </label>
          <textarea
            id={`${idLista}-comentariu`}
            className="input min-h-16 py-1"
            rows={2}
            placeholder="De ce se plătește, ce factură acoperă…"
            value={comentariu}
            onChange={(e) => setComentariu(e.target.value)}
          />
          <span className="text-11 text-ink-3">
            Apare în panoul de detalii al plății, în același fir cu restul comentariilor.
          </span>
        </div>

        {adauga.isError ? (
          <p className="text-11 text-neg" role="alert">
            {adauga.error.message}
          </p>
        ) : null}

        <footer className="flex items-center justify-end gap-2">
          <button type="button" className="hit-24 chip" onClick={onClose}>
            Renunță
          </button>
          <button
            type="button"
            className="hit-24 chip font-medium text-ink-1"
            disabled={!poateTrimite}
            onClick={trimite}
          >
            {adauga.isPending ? 'Adaug…' : 'Adaugă plata'}
          </button>
        </footer>
      </div>
    </div>
  );
}
